import React, { useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useClient } from '../ClientContext';

const SSO_REAUTH_STORAGE_KEY = 'sso:forced-role-reauth';
const SSO_REAUTH_QUERY_PARAM = 'sso_reauth';
const SSO_RETURN_TO_QUERY_PARAM = 'sso_return_to';

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, isAuthenticated, isLoading, logout } = useAuth0();
  const { config } = useClient();
  const userRoles = user ? user[process.env.REACT_APP_AUTH0_ROLE_CLAIMS] || [] : [];
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const map = useMemo(() => (config && config.roleRedirectMap) || {}, [config]);
  const matchedRole = useMemo(() => userRoles.find((role) => Boolean(map[role])), [map, userRoles]);
  const hasRequiredRole = userRoles.includes(requiredRole);
  const reauthKey = `${SSO_REAUTH_STORAGE_KEY}:${requiredRole}`;

  useEffect(() => {
    if (hasRequiredRole) {
      window.sessionStorage.removeItem(reauthKey);
    }
  }, [hasRequiredRole, reauthKey]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !config || hasRequiredRole) {
      return;
    }

    const hasForcedReauth = window.sessionStorage.getItem(reauthKey) === '1';

    if (hasForcedReauth) {
      return;
    }

    window.sessionStorage.setItem(reauthKey, '1');

    const logoutReturnToUrl = new URL(window.location.origin);
    logoutReturnToUrl.searchParams.set(SSO_REAUTH_QUERY_PARAM, '1');
    logoutReturnToUrl.searchParams.set(SSO_RETURN_TO_QUERY_PARAM, encodeURIComponent(currentPath));

    logout({
      logoutParams: {
        returnTo: logoutReturnToUrl.toString(),
      },
    });
  }, [
    config,
    currentPath,
    hasRequiredRole,
    isAuthenticated,
    isLoading,
    matchedRole,
    logout,
    reauthKey,
    requiredRole,
    user?.email,
    userRoles,
  ]);

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!config) {
    return null;
  }

  if (!hasRequiredRole) {
    const hasForcedReauth = window.sessionStorage.getItem(reauthKey) === '1';

    if (!hasForcedReauth) {
      return null;
    }

    const redirectTo = map[matchedRole] || '/access-denied';

    if (!redirectTo.startsWith('/')) {
      window.location.href = redirectTo;
    } else {
      return <Navigate to={redirectTo} />;
    }
    return null;
  }

  return children;
};

export default ProtectedRoute;
